import { useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import styles from './DayBook.module.css';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Snackbar from '@mui/material/Snackbar';
import * as Yup from 'yup';

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    MenuItem,
    Box,
    Typography,
    Grid,
    InputAdornment,
    TablePagination
} from '@mui/material';
import {
    dayBookListApi,
    deleteDayBookApi,
    editLedgerEntryApi,
    getEntityApi,
    ledgerEntryApi
} from 'utils/API/SuperdistributorEmployee APIs/SuperdistributorApiCalls';
import { globalNotifyError, globalNotifySuccess } from 'views/utilities/AlertToast';
import { LoadingButton } from '@mui/lab';

const DenominationInput = ({ label, value, onChange, isPremium = false }) => (
    <Grid item xs={12} sm={6} md={4}>
        {' '}
        {/* Responsive grid items */}
        <Box className={`${styles['denomination-input-wrapper']} ${isPremium ? styles.premium : ''}`}>
            <TextField
                label={label}
                type="number"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                fullWidth
                size="small"
                InputProps={{
                    startAdornment: <InputAdornment position="start">{label.split(' ')[0]} x</InputAdornment>,
                    inputProps: { min: 0 } // Ensure non-negative input
                }}
            />
            {isPremium && <span className={styles['premium-label']}>Premium</span>}
        </Box>
    </Grid>
);
DenominationInput.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    onChange: PropTypes.func.isRequired,
    isPremium: PropTypes.bool
};

const DayBook = () => {
    const [open, setOpen] = useState(false);
    const [to, setTo] = useState('');
    const [from, setFrom] = useState('');
    const [toEntity, setToEntity] = useState('');
    const [entities, setEntities] = useState([]);
    const [fromEntity, setFromEntity] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0); // from API
    const [loading, setLoading] = useState(false);
    const [premiumCharges, setPremiumCharges] = useState('');
    const [showPremiumChargesField, setShowPremiumChargesField] = useState(false);

    const [notes, setNotes] = useState('');

    const [fromDate, setFromDate] = useState(new Date());
    const [toDate, setToDate] = useState(new Date());
    const [status, setStatus] = useState('all');
    const [transactions, setTransactions] = useState([]);

    const [selectedLedgerDate, setSelectedLedgerDate] = useState(new Date());

    const [denomination50000, setDenomination50000] = useState(0);
    const [denomination20000, setDenomination20000] = useState(0);
    const [denomination500, setDenomination500] = useState(0);
    const [denomination200, setDenomination200] = useState(0);
    const [denomination100, setDenomination100] = useState(0);
    const [denomination50, setDenomination50] = useState(0);
    const [denomination20, setDenomination20] = useState(0);
    const [denomination10, setDenomination10] = useState(0);
    const [coins, setCoins] = useState(0);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [selectedTxn, setSelectedTxn] = useState(null);

    const validationSchema = Yup.object().shape({
        to: Yup.string().required('To is required'),

        to_entity: Yup.string().when('to', {
            is: (val) => val && val !== 'myself' && val !== 'expenses',
            then: (schema) => schema.required('To Entity is required'),
            otherwise: (schema) => schema.notRequired()
        }),

        from: Yup.string().when('to', {
            is: (val) => val === 'myself',
            then: (schema) => schema.required('From is required'),
            otherwise: (schema) => schema.notRequired()
        }),

        from_entity: Yup.string().when('to', {
            is: (val) => val === 'myself',
            then: (schema) => schema.required('From Entity is required'),
            otherwise: (schema) => schema.notRequired()
        }),

        note: Yup.string().required('Note is required'),

        date: Yup.string().required('Date is required'),

        denominations: Yup.object().test('at-least-one', 'At least one denomination is required', (value) => {
            return Object.values(value || {}).some((v) => Number(v) > 0);
        })
    });

    const fetchTransactions = async (page = currentPage, size = pageSize) => {
        setLoading(true);
        try {
            const payload = {
                fromDate: fromDate.toISOString().split('T')[0],
                toDate: toDate ? toDate.toISOString().split('T')[0] : '',
                pageSize: size.toString(),
                pageNumber: page.toString(),
                status: status, // pending, approved, rejected, all
                is_pending_approval: false,
                is_myself_approval: false
            };

            const res = await dayBookListApi(payload);

            setTransactions(res.data?.data || []);
            setTotalCount(res.data?.count || 0); // <- Add this line
        } catch (err) {
            console.error('Error fetching transactions', err);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        fetchTransactions(currentPage, pageSize);
        // eslint-disable-next-line
    }, [currentPage, pageSize, status]);
    const handleRowsPerPageChange = (event) => {
        setPageSize(parseInt(event.target.value, 10));
        setCurrentPage(1); // Reset to the first page when rows per page changes
    };
    const handlePageChange = (event, newPage) => {
        // TablePagination is 0-indexed, so we add 1 for our 1-indexed state
        setCurrentPage(newPage + 1);
    };

    const handleSubmit = async () => {
        const denominations = {
            50000: Number(denomination50000),
            20000: Number(denomination20000),
            500: Number(denomination500),
            200: Number(denomination200),
            100: Number(denomination100),
            50: Number(denomination50),
            20: Number(denomination20),
            10: Number(denomination10),
            coins: Number(coins)
        };

        // ✅ Define isEdit safely
        const isEdit = selectedTxn && selectedTxn.eid ? true : false;
        console.log('Mode:', isEdit ? 'Edit' : 'Create');

        const payload = {
            ...(isEdit && { id: selectedTxn.eid }),
            to: to,
            note: notes,
            date: selectedLedgerDate?.toLocaleDateString('en-CA'),
            denominations,
            ...(to === 'myself'
                ? {
                      from: from,
                      from_entity: fromEntity
                  }
                : {
                      to_entity: toEntity
                  }),
            ...(premiumCharges !== '' && {
                premium_charge: Number(premiumCharges) || 0
            })
        };

        try {
            await validationSchema.validate(payload, { abortEarly: false });

            // ✅ Use isEdit here safely
            const response = isEdit ? await editLedgerEntryApi(payload) : await ledgerEntryApi(payload);

            if (response.status === 200) {
                globalNotifySuccess(isEdit ? 'Successfully updated!' : 'Successfully submitted!');
                setOpen(false);
                resetForm();

                // ✅ Force refresh list after successful create/edit
                setStatus('all');
                setCurrentPage(1);
                fetchTransactions(1, pageSize, 'all');
            }
        } catch (err) {
            if (err.name === 'ValidationError') {
                err.inner.forEach((e) => {
                    globalNotifyError(e.message);
                });
            } else {
                console.error('API Error:', err);
                globalNotifyError('Failed to submit!');
            }
        }
    };

    // const Alert = React.forwardRef(function Alert(props, ref) {
    //     return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
    // });
    const resetForm = () => {
        setTo('');
        setFrom('');
        setToEntity('');
        setFromEntity('');
        setNotes('');
        setSelectedLedgerDate(null);
        setDenomination50000(0);
        setDenomination20000(0);
        setDenomination500(0);
        setDenomination200(0);
        setDenomination100(0);
        setDenomination50(0);
        setDenomination20(0);
        setDenomination10(0);
        setCoins(0);
        setEntities([]);
        setPremiumCharges('');
        setShowPremiumChargesField(false);
    };

    useEffect(() => {
        const fetchEntities = async () => {
            if (to && to !== 'myself') {
                try {
                    const payload = { type: to.toLowerCase() }; // client, vendor, employee
                    const response = await getEntityApi(payload); // adapt this if needed

                    setEntities(Array.isArray(response.data?.data) ? response.data.data : []);
                } catch (error) {
                    console.error('Failed to fetch entities:', error);
                    setEntities([]);
                }
            } else {
                setEntities([]);
            }
        };

        fetchEntities();
    }, [to]);
    useEffect(() => {
        const fetchFromEntities = async () => {
            if (to === 'myself' && from) {
                try {
                    const payload = { type: from.toLowerCase() };
                    const response = await getEntityApi(payload);
                    setEntities(Array.isArray(response.data?.data) ? response.data.data : []);
                } catch (error) {
                    console.error('Failed to fetch from entities:', error);
                    setEntities([]);
                }
            }
        };

        fetchFromEntities();
    }, [to, from]);
    useEffect(() => {
        if (Number(denomination50000) > 0 || Number(denomination20000) > 0) {
            setShowPremiumChargesField(true);
        } else {
            setShowPremiumChargesField(false);
            if (premiumCharges !== '') {
                setPremiumCharges('');
            }
        }
    }, [denomination50000, denomination20000, premiumCharges]);

    const handleClose = () => {
        setOpen(false);

        resetForm();
    };
    const totalDenominationAmount = useMemo(() => {
        const denominationTotal =
            (Number(denomination50000) || 0) * 50000 +
            (Number(denomination20000) || 0) * 20000 +
            (Number(denomination500) || 0) * 500 +
            (Number(denomination200) || 0) * 200 +
            (Number(denomination100) || 0) * 100 +
            (Number(denomination50) || 0) * 50 +
            (Number(denomination20) || 0) * 20 +
            (Number(denomination10) || 0) * 10 +
            (Number(coins) || 0);

        const premium = premiumCharges !== '' ? Number(premiumCharges) || 0 : 0;

        return denominationTotal + premium;
    }, [
        denomination50000,
        denomination20000,
        denomination500,
        denomination200,
        denomination100,
        denomination50,
        denomination20,
        denomination10,
        coins,
        premiumCharges
    ]);
    const handleDeleteRemark = async (eid) => {
        try {
            const payload = { id: eid };
            const response = await deleteDayBookApi(payload);
            console.log('Delete API response:', response);

            if (response?.data?.success) {
                fetchTransactions(currentPage, pageSize);
            } else {
                console.error(' Failed to delete remark:', response?.data?.message || 'No message');
            }
        } catch (error) {
            console.error('Error while deleting remark:', error);
        }
    };
    useEffect(() => {
        if (selectedTxn) {
            setTo(selectedTxn.to || '');
            setFrom(selectedTxn.from || '');
            setFromEntity(selectedTxn.from_entity || '');
            setToEntity(selectedTxn.to_entity || '');
            setNotes(() => {
                const parsed = JSON.parse(selectedTxn.note || '[]');
                return parsed.length ? parsed[parsed.length - 1].message : '';
            });
            setSelectedLedgerDate(new Date(selectedTxn.txn_date));
            setPremiumCharges(selectedTxn.premium_charge || '');

            // Set denominations
            const denoms = JSON.parse(selectedTxn.denominations || '{}');
            setDenomination10(denoms['10'] || 0);
            setDenomination20(denoms['20'] || 0);
            setDenomination50(denoms['50'] || 0);
            setDenomination100(denoms['100'] || 0);
            setDenomination200(denoms['200'] || 0);
            setDenomination500(denoms['500'] || 0);
            setDenomination20000(denoms['20000'] || 0);
            setDenomination50000(denoms['50000'] || 0);
            setCoins(denoms['coins'] || 0);
        }
    }, [selectedTxn]);

    return (
        <div className={styles.container}>
            <Box sx={{ padding: 2 }}>
                {/* First Section: Day Book & Pending Approvals */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'space-between', // space between left and right groups
                        alignItems: { xs: 'stretch', sm: 'center' },
                        flexWrap: 'wrap',
                        gap: 2,
                        mb: 3
                    }}
                >
                    {/* Left Side: Day Book & Pending Approvals */}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button variant="contained" disableElevation className={styles.daybookbtn}>
                            Day Book
                        </Button>
                    </Box>

                    {/* Right Side: New Ledger Entry */}
                    <Box>
                        <button
                            className={styles.addClientButton}
                            onClick={() => {
                                resetForm();

                                setOpen(true);
                            }}
                        >
                            + New Ledger Entry
                        </button>
                    </Box>
                </Box>

                {/* Second Section: Date Picker + Submit */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { sm: 'center' },
                        gap: 2,
                        mb: 3
                    }}
                >
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <Box display="flex" gap={2}>
                            <DatePicker
                                label="From Date"
                                value={fromDate}
                                onChange={(newValue) => setFromDate(newValue)}
                                maxDate={new Date()}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        variant: 'outlined',
                                        sx: { width: '180px' }
                                    }
                                }}
                            />
                            <DatePicker
                                label="To Date"
                                value={toDate}
                                onChange={(newValue) => setToDate(newValue)}
                                minDate={fromDate}
                                maxDate={new Date()}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        variant: 'outlined',
                                        sx: { width: '180px' }
                                    }
                                }}
                            />
                        </Box>
                    </LocalizationProvider>
                    <TextField
                        label="Status"
                        select
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        fullWidth
                        size="small"
                        sx={{ width: '180px' }}
                    >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="approved">Approved</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>

                        <MenuItem value="rejected">Rejected</MenuItem>
                    </TextField>

                    <LoadingButton onClick={() => fetchTransactions(1, pageSize)} className={styles.addClientButton} loading={loading}>
                        Submit
                    </LoadingButton>
                </Box>

                {/* ...Your table and remaining content... */}
            </Box>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth className={styles['ledger-entry-dialog']}>
                    <DialogTitle className={styles['dialog - title']}>New Ledger Entry</DialogTitle>
                    <DialogContent dividers className={styles['dialog - content']}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 3 }}>
                            <TextField label="To" select value={to} onChange={(e) => setTo(e.target.value)} fullWidth size="small">
                                <MenuItem value="client">Client</MenuItem>
                                <MenuItem value="vendor">Vendor</MenuItem>
                                <MenuItem value="employee">Employee</MenuItem>
                                <MenuItem value="myself">Myself</MenuItem>
                                <MenuItem value="expenses">Expense</MenuItem>
                            </TextField>

                            {/* 👇 Conditional Rendering */}
                            {to === 'myself' ? (
                                <>
                                    <TextField
                                        label="From"
                                        select
                                        value={from}
                                        onChange={(e) => setFrom(e.target.value)}
                                        fullWidth
                                        size="small"
                                    >
                                        <MenuItem value="vendor">Vendor</MenuItem>
                                        <MenuItem value="employee">Employee</MenuItem>
                                        <MenuItem value="client">Client</MenuItem>
                                    </TextField>

                                    <TextField
                                        label="From Entity"
                                        select
                                        value={fromEntity}
                                        onChange={(e) => setFromEntity(e.target.value)}
                                        fullWidth
                                        size="small"
                                    >
                                        {entities.map((entity) => (
                                            <MenuItem key={entity.id} value={entity.id}>
                                                {entity.name}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </>
                            ) : to !== 'expenses' ? ( // Show To Entity only if to is NOT 'myself' or 'expense'
                                <TextField
                                    label="To Entity"
                                    select
                                    value={toEntity}
                                    onChange={(e) => setToEntity(e.target.value)}
                                    fullWidth
                                    size="small"
                                >
                                    {entities.map((entity) => (
                                        <MenuItem key={entity.id} value={entity.id}>
                                            {entity.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            ) : null}

                            <TextField
                                label="Notes *"
                                placeholder="Optional notes for this transaction"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                fullWidth
                                size="small"
                                required
                            />
                            <DatePicker
                                label="Transaction Date"
                                select
                                value={selectedLedgerDate}
                                onChange={(newValue) => setSelectedLedgerDate(newValue)}
                                maxDate={new Date()}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        fullWidth: true
                                    }
                                }}
                            />
                            {showPremiumChargesField && (
                                <TextField
                                    label="Premium Charges"
                                    type="number"
                                    value={premiumCharges}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (value === '' || /^\d+$/.test(value)) {
                                            setPremiumCharges(value);
                                        }
                                    }}
                                    fullWidth
                                    size="small"
                                    InputProps={{
                                        startAdornment: <Typography sx={{ mr: 0.5 }}>₹</Typography>
                                    }}
                                />
                            )}
                        </Box>

                        <Typography variant="h6" className={styles['denomination-heading']}>
                            Denomination
                        </Typography>
                        <Typography variant="subtitle2" className={styles['denomination-subheading']}>
                            Denomination Details
                        </Typography>

                        <Grid container spacing={2} className={styles['denomination - grid']}>
                            <DenominationInput
                                label={to === 'client' || to === 'vendor' ? '₹50,000 (Premium)' : '₹50,000'}
                                value={denomination50000}
                                onChange={setDenomination50000}
                                isPremium={to === 'client' || to === 'vendor'} // Only apply premium style if to === 'client'
                                additionalClass={styles.daybookbtn}
                            />
                            <DenominationInput
                                label={to === 'client' || to === 'vendor' ? '₹20,000 (Premium)' : '₹20,000'}
                                value={denomination20000}
                                onChange={setDenomination20000}
                                isPremium={to === 'client' || to === 'vendor'}
                                additionalClass={styles.daybookbtn}
                            />
                            <DenominationInput label="₹500" value={denomination500} onChange={setDenomination500} />
                            <DenominationInput label="₹200" value={denomination200} onChange={setDenomination200} />
                            <DenominationInput label="₹100" value={denomination100} onChange={setDenomination100} />
                            <DenominationInput label="₹50" value={denomination50} onChange={setDenomination50} />
                            <DenominationInput label="₹20" value={denomination20} onChange={setDenomination20} />
                            <DenominationInput label="₹10" value={denomination10} onChange={setDenomination10} />
                            <DenominationInput label="Coins (₹)" value={coins} onChange={setCoins} />
                        </Grid>
                        <Box mt={2}>
                            <TextField
                                label="Total Amount"
                                value={`₹${totalDenominationAmount.toLocaleString()}`}
                                fullWidth
                                size="small"
                                InputProps={{
                                    readOnly: true
                                }}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions className={styles['dialog - actions']}>
                        <Button
                            onClick={() => {
                                resetForm();
                                setOpen(false);
                            }}
                            color="inherit"
                        >
                            Cancel
                        </Button>
                        <Button onClick={() => setShowConfirmation(true)} className={styles.addClientButton}>
                            Submit
                        </Button>
                    </DialogActions>
                </Dialog>
            </LocalizationProvider>
            <Dialog open={showConfirmation} onClose={() => setShowConfirmation(false)}>
                <DialogTitle>Confirm Submission</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to submit this ledger entry?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowConfirmation(false)} color="inherit">
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        onClick={() => {
                            setShowConfirmation(false);
                            handleSubmit();
                        }}
                        className={styles.addClientButton}
                        color="primary"
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
            <Snackbar
                open={showSuccessMessage}
                autoHideDuration={3000}
                onClose={() => setShowSuccessMessage(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            ></Snackbar>

            <motion.div
                className={styles.tableSection}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.5 }}
            >
                <Box className={styles.tabContent}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Sl. No.</th>
                                <th>Date</th>
                                <th>Particulars</th>
                                {/* <th>Opening Balance</th> */}
                                <th>Cash in</th>
                                <th>Cash out</th>
                                <th>Denominations</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Array.isArray(transactions) && transactions.length > 0 ? (
                                transactions.map((tx, index) => (
                                    <tr key={index}>
                                        <td>{(currentPage - 1) * pageSize + index + 1}</td>
                                        <td>{tx.txn_date}</td>
                                        <td>
                                            {tx.from_entity_name || tx.from || 'myself'}
                                            <br />
                                            To: {tx.to_entity_name || tx.to || 'myself'}
                                        </td>

                                        {/* <td>{tx.opening_balance}</td> */}
                                        <td>₹{tx.cash_in}</td>
                                        <td>
                                            ₹{tx.cash_out}
                                            {parseFloat(tx.premium_charge) > 0 && (
                                                <span style={{ fontSize: '14px', color: '#888' }}> (+₹{tx.premium_charge} premium)</span>
                                            )}
                                        </td>

                                        <td>
                                            <div className={styles.denominationGrid}>
                                                {tx.denominations &&
                                                    Object.entries(JSON.parse(tx.denominations)).map(([note, qty], i) => (
                                                        <div
                                                            key={i}
                                                            style={{
                                                                fontSize: '14px',
                                                                color: Number(qty) === 0 ? 'red' : 'inherit'
                                                            }}
                                                        >
                                                            {note} × {qty}
                                                        </div>
                                                    ))}
                                            </div>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <Box display="flex" justifyContent="center" gap={1}>
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    className={styles.deleteButton}
                                                    onClick={() => {
                                                        if (window.confirm('Are you sure you want to delete this Data?')) {
                                                            handleDeleteRemark(tx.eid); // send encrypted ID
                                                        }
                                                    }}
                                                >
                                                    Delete
                                                </Button>

                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    className={styles.addClientButton}
                                                    onClick={() => {
                                                        setSelectedTxn(tx);
                                                        setOpen(true);
                                                        resetForm();
                                                    }}
                                                >
                                                    Edit
                                                </Button>
                                            </Box>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>
                                        No transactions found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        component="div"
                        count={totalCount}
                        rowsPerPage={pageSize}
                        page={currentPage - 1} // TablePagination is 0-indexed
                        onPageChange={handlePageChange}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        sx={{
                            marginTop: 2,
                            display: 'flex',
                            justifyContent: 'flex-end',
                            borderTop: '1px solid #e0e0e0',
                            paddingTop: '8px'
                        }}
                    />
                </Box>
            </motion.div>
        </div>
    );
};

export default DayBook;
